/*
  Product page add-on panel controller.

  The rows and tiles here are the upsell sheet's own markup, so this file drives
  them the way wb-upsell-v2.js drives them: the same drawer mechanics, the same
  chip, the same sub lines. Where a rule is duplicated from that file it is
  duplicated deliberately, because the alternative is loading a controller that
  wants to mount an overlay, fetch a view and run its own add to cart.

  It stays out of product-addons.min.js's way. That file scopes its product page
  work to `product-component product-addons [data-addon-card]`, which this markup
  does not provide, so the two never fight over the add-to-cart label. It still
  owns the cart drawer.

  Three things carry money and are worth reading before changing:

  1. properties[_addon_variant_ids] and properties[_free_addon_variant_ids] are
     what add to cart and the cart transform function actually read. They are
     created here because that is where the old controller created them too.
  2. Exactly one closure, always: the buckle and clasp rows share the radio name
     `pdp-closure`, so it is structural rather than enforced here. The name is
     the panel's own and must never match the sheet's `wb-closure`: neither set
     of radios has a form owner, so a shared name makes them one group across the
     page and each surface silently unchecks the other.
  3. The running total starts from data-atc-base-price, which
     product-form.liquid already writes.
*/
(function () {
  'use strict';

  var CLOSURE_GROUP = 'pdp-closure';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function t(key, fallback) {
    var strings = window.wbUpsellV2Strings || {};
    return strings[key] || fallback;
  }

  /*
    Formats like Liquid's money_without_trailing_zeros, the same as
    wb-upsell-v2.js, which is deliberate: the panel and the sheet show the same
    prices side by side and must not disagree about them.

    The thousands separator and the decimal mark are separate arguments. The
    first version here reused one for both and then rewrote the first occurrence,
    which turned 1234.56 into "1,234.56" under a comma format instead of
    "1.234,56", and dropped the decimal mark entirely under a space format.
  */
  function money(cents) {
    var pattern =
      (window.theme && window.theme.moneyFormat) ||
      (window.Shopify && window.Shopify.money_format) ||
      '{{amount_no_decimals_with_space_separator}} kr';
    var value = Number(cents) || 0;

    function withSeparators(decimals, thousands, decimalMark) {
      var fixed = (value / 100).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      if (!parts[1] || Number(parts[1]) === 0) return whole;
      return whole + decimalMark + parts[1];
    }

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount_no_decimals':
          return withSeparators(0, ',', '.');
        case 'amount_with_comma_separator':
          return withSeparators(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(0, '.', ',');
        case 'amount_with_space_separator':
          return withSeparators(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withSeparators(0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return withSeparators(2, "'", '.');
        default:
          return withSeparators(2, ',', '.');
      }
    });
  }

  /*
    The words every title in a row shares, and what is left of each once they are
    taken away. Copied from wb-upsell-v2.js for the same reason the rest of this
    file is: the sheet's script is fetched on demand and may never load, and the
    row is unreadable until this has run.
  */
  function splitCommonWords(titles) {
    if (titles.length < 2) return { shared: '', unique: titles.slice() };

    var split = titles.map(function (title) {
      return title.split(/\s+/).filter(Boolean);
    });

    var lead = 0;
    while (
      split.every(function (words) {
        return words.length > lead + 1 && words[lead].toLowerCase() === split[0][lead].toLowerCase();
      })
    ) {
      lead += 1;
    }

    var tail = 0;
    while (
      split.every(function (words) {
        var index = words.length - 1 - tail;
        var reference = split[0][split[0].length - 1 - tail];
        return index > lead && words[index].toLowerCase() === reference.toLowerCase();
      })
    ) {
      tail += 1;
    }

    if (!lead && !tail) return { shared: '', unique: titles.slice() };

    var sharedWords = split[0].slice(0, lead).concat(tail ? split[0].slice(split[0].length - tail) : []);
    var unique = split.map(function (words) {
      var middle = words.slice(lead, tail ? words.length - tail : words.length);
      return middle.join(' ').replace(/^[\s\-\u2013\u2014\u00b7,]+|[\s\-\u2013\u2014\u00b7,]+$/g, '');
    });

    return {
      shared: sharedWords.join(' ').replace(/^[\s\-\u2013\u2014\u00b7,]+|[\s\-\u2013\u2014\u00b7,]+$/g, ''),
      unique: unique.map(function (value, index) {
        return value || titles[index];
      }),
    };
  }

  function Panel(root) {
    this.root = root;
    this.form = null;
    this.shortenLabels();
    this.bind();
    this.sync();
  }

  /*
    A row whose tiles are sibling products takes its title from the words those
    titles share, and each tile keeps only what distinguishes it. Without it the
    buckle row is headed "Premium Buckle - Silver" while the free black one is
    the finish actually selected, which reads as the wrong finish being chosen,
    and every tile repeats the row's own name.

    data-wb-short-label is the finish on its own, which is what a row has to
    compare against to answer "is the clasp in the same finish as the buckle".

    Run once at mount: it only rewrites text.
  */
  Panel.prototype.shortenLabels = function () {
    this.root.querySelectorAll('[data-wb-option]').forEach(function (option) {
      var tiles = toArray(option.querySelectorAll('[data-wb-tile]'));
      if (tiles.length < 2) return;

      var titles = tiles.map(function (tile) {
        return tile.getAttribute('data-wb-label') || '';
      });
      var parts = splitCommonWords(titles);
      if (!parts.shared) return;

      tiles.forEach(function (tile, index) {
        var name = tile.querySelector('[data-wb-tile-name]');
        if (name) name.textContent = parts.unique[index];
        tile.setAttribute('data-wb-short-label', parts.unique[index]);
      });

      var rowTitle = option.querySelector('[data-wb-row-title]');
      if (rowTitle) rowTitle.textContent = parts.shared;
    });
  };

  Panel.prototype.tilesIn = function (option) {
    return toArray(option.querySelectorAll('[data-wb-tile]'));
  };

  Panel.prototype.chosenIn = function (option) {
    return this.tilesIn(option).filter(function (tile) {
      var input = tile.querySelector('[data-wb-tile-input]');
      return input && input.checked;
    })[0];
  };

  // What the row previews when nothing is chosen: the free tile if there is one,
  // otherwise the first. Without this a row advertises whichever finish the
  // server happened to render first.
  Panel.prototype.preferredTileIn = function (option) {
    var tiles = this.tilesIn(option);
    return tiles.filter(function (tile) {
      return tile.getAttribute('data-wb-free') === 'true';
    })[0] || tiles[0];
  };

  /*
    The form the panel's hidden inputs belong to.

    Scoped to the panel's own product-component. It used to fall back to the
    whole document, which picks the first product form on the page: on a template
    carrying quick-add cards that is not the form being submitted, and the add-on
    ids would have ridden along with someone else's product. With no
    product-component to scope to, a single product form on the page is
    unambiguous and anything else is left alone.
  */
  Panel.prototype.productForm = function () {
    if (this.form && document.contains(this.form)) return this.form;

    var component = this.root.closest('product-component');
    if (component) {
      this.form = component.querySelector('form[data-product-form]');
      return this.form;
    }

    var forms = document.querySelectorAll('form[data-product-form]');
    this.form = forms.length === 1 ? forms[0] : null;
    return this.form;
  };

  Panel.prototype.hiddenInput = function (name) {
    var form = this.productForm();
    if (!form) return null;
    var input = form.querySelector('input[name="properties[' + name + ']"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      form.appendChild(input);
    }
    return input;
  };

  Panel.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('change', function (event) {
      if (event.target && event.target.matches('[data-wb-tile-input]')) self.sync();
    });

    /*
      Radios cannot clear themselves, and a tool or storage choice has to be
      removable: those tabs allow at most one selection and both may be empty.
      Mousedown, so the state is read before the browser checks the radio.
      The closure is left alone, because one of the two must always be selected.
    */
    this.root.addEventListener('mousedown', function (event) {
      // Left button only. Any button cleared the tile and the preventDefault
      // below then swallowed the context menu, so a right-click on a chosen tool
      // both deselected it and refused to explain itself.
      if (event.button !== 0) return;
      var tile = event.target.closest('[data-wb-tile]');
      if (!tile) return;
      var input = tile.querySelector('[data-wb-tile-input]');
      if (!input || !input.checked) return;
      if (input.name === CLOSURE_GROUP) return;
      event.preventDefault();
      input.checked = false;
      self.sync();
    });

    this.root.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-pdp-tab]');
      if (tab) {
        self.showTab(tab.getAttribute('data-pdp-tab'));
        return;
      }

      /*
        Tapping the row body selects it, using whichever tile it opens on.

        Without this a row whose drawer is shut cannot be selected at all: its
        tiles are the only inputs, and they are inside the drawer that only
        opens once the row is selected. That left the clasp, every tool and
        every storage row inert, because the buckle is the only row that starts
        open.
      */
      var card = event.target.closest('[data-wb-card]');
      if (!card || event.target.closest('[data-wb-tile]')) return;

      var option = card.closest('[data-wb-option]');
      if (!option || self.chosenIn(option)) return;

      var preferred = self.preferredTileIn(option);
      var input = preferred && preferred.querySelector('[data-wb-tile-input]');
      if (!input) return;
      input.checked = true;
      self.sync();
    });

    /*
      The keyboard's way to clear a tool or storage choice. Deselecting was
      mousedown-only, so a keyboard user could pick one of those and never take
      it off again. Space on a checked tile clears it, which is the same rule the
      pointer follows; on an unchecked one the browser's own behaviour stands.
    */
    this.root.addEventListener('keydown', function (event) {
      var input = event.target.closest('[data-wb-tile-input]');
      if (input && event.key === ' ' && input.checked && input.name !== CLOSURE_GROUP) {
        event.preventDefault();
        input.checked = false;
        self.sync();
        return;
      }

      var tab = event.target.closest('[data-pdp-tab]');
      if (!tab) return;
      var step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      var tabs = toArray(self.root.querySelectorAll('[data-pdp-tab]'));
      var next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
      self.showTab(next.getAttribute('data-pdp-tab'));
      next.focus();
    });
  };

  Panel.prototype.showTab = function (key) {
    this.root.querySelectorAll('[data-pdp-tab]').forEach(function (tab) {
      var on = tab.getAttribute('data-pdp-tab') === key;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    this.root.querySelectorAll('[data-pdp-panel]').forEach(function (panel) {
      panel.dataset.active = panel.getAttribute('data-pdp-panel') === key ? 'true' : 'false';
    });
  };

  // Mirrors wb-upsell-v2.js: drawer state, selected classes, row preview, chip
  // and the two role-specific sub lines.
  Panel.prototype.syncRows = function () {
    var self = this;
    this.root.querySelectorAll('[data-wb-option]').forEach(function (option) {
      var tiles = self.tilesIn(option);
      var chosen = self.chosenIn(option);
      var isSelected = !!chosen;
      var card = option.querySelector('[data-wb-card]');
      var drawer = option.querySelector('[data-wb-drawer]');

      option.classList.toggle('is-selected', isSelected);
      if (card) card.classList.toggle('is-selected', isSelected);

      if (drawer) {
        // A single-tile row has nothing to open, so it stays out of the layout.
        var canOpen = tiles.length > 1;
        drawer.hidden = !canOpen;
        drawer.classList.toggle('is-open', canOpen && isSelected);
      }

      var shown = chosen || self.preferredTileIn(option);
      if (!card || !shown) return;

      var image = card.querySelector('[data-wb-row-image]');
      var src = shown.getAttribute('data-wb-image');
      if (image && src && image.getAttribute('src') !== src) image.src = src;

      var chip = card.querySelector('[data-wb-chip]');
      if (chip) {
        chip.textContent =
          shown.getAttribute('data-wb-free') === 'true'
            ? t('free', 'FREE')
            : '+' + money(Number(shown.getAttribute('data-wb-price')) || 0);
      }

      var sub = card.querySelector('[data-wb-sub]');
      var role = card.getAttribute('data-wb-role');
      if (!sub) return;

      /*
        The only sub line that earns its space is the one telling a customer
        something is already theirs. "Replaces the free buckle", on the buckle
        row and on the clasp, describes plumbing the customer did not ask about
        and reads as a warning next to a price. So: the included line when the
        shown finish is free, nothing otherwise.

        The sheet still shows both, deliberately, because there it is walking
        someone through a decision rather than sitting under a product title.
      */
      var isFreeChoice = shown.getAttribute('data-wb-free') === 'true';
      if (role === 'buckle' && isFreeChoice) {
        sub.hidden = false;
        sub.textContent = t('included_with_strap', 'INCLUDED WITH THE STRAP');
      } else {
        sub.hidden = true;
        sub.textContent = '';
      }
    });
  };

  /*
    Carried across a variant change.

    Only the paid ones. The buckle, clasp, tool and storage lists are
    product-level metafields, so the same tiles come back for every variant and a
    paid choice survives a colour change. The free buckle is per-variant, so its
    tile is left to whatever the server rendered for the variant now selected:
    forcing the old one back is how a 22mm strap ends up shipping a 20mm buckle.
  */
  Panel.prototype.rememberPaidSelections = function () {
    this.remembered = toArray(this.root.querySelectorAll('[data-wb-tile-input]:checked'))
      .map(function (input) {
        return input.closest('[data-wb-tile]');
      })
      .filter(function (tile) {
        return tile && tile.getAttribute('data-wb-free') !== 'true';
      })
      .map(function (tile) {
        return tile.getAttribute('data-wb-variant-id');
      });
  };

  /*
    Take up the markup the theme just rendered for the new variant.

    renderProductInfo replaces this panel's innerHTML, so the labels are back to
    their full form and the tiles are back to the server's state. A tile that no
    longer exists is simply not restored.
  */
  Panel.prototype.adopt = function () {
    this.shortenLabels();

    var root = this.root;
    (this.remembered || []).forEach(function (variantId) {
      var input = root.querySelector('[data-wb-tile][data-wb-variant-id="' + variantId + '"] [data-wb-tile-input]');
      if (input) input.checked = true;
    });
    this.remembered = null;

    this.sync();
  };

  Panel.prototype.sync = function () {
    this.syncRows();

    var paid = [];
    var free = [];
    var total = 0;

    this.root.querySelectorAll('[data-wb-tile-input]:checked').forEach(function (input) {
      var tile = input.closest('[data-wb-tile]');
      if (!tile) return;
      var id = tile.getAttribute('data-wb-variant-id') || input.value;
      if (tile.getAttribute('data-wb-free') === 'true') {
        free.push(id);
      } else {
        paid.push(id);
        total += Number(tile.getAttribute('data-wb-price')) || 0;
      }
    });

    var paidInput = this.hiddenInput('_addon_variant_ids');
    if (paidInput) paidInput.value = paid.join(',');
    var freeInput = this.hiddenInput('_free_addon_variant_ids');
    if (freeInput) freeInput.value = free.join(',');

    this.updateTotal(total);

    /*
      Announced so anything outside the panel can follow the selection.

      A `change` event is not enough on its own: selecting a row by its body,
      clearing one with mousedown or Space, and re-adopting the markup after a
      variant re-render all set `checked` in script, which fires nothing. A
      mirror listening for `change` sees a user's click on a tile and misses
      every other way the same selection is made.
    */
    this.root.dispatchEvent(
      new CustomEvent('pdp-addons:changed', {
        bubbles: true,
        detail: { paid: paid.slice(), free: free.slice() },
      })
    );
  };

  Panel.prototype.updateTotal = function (addonCents) {
    var form = this.productForm();
    if (!form) return;
    form.querySelectorAll('[data-atc-base-price]').forEach(function (button) {
      var base = Number(button.getAttribute('data-atc-base-price'));
      if (!Number.isFinite(base)) return;
      var label = button.getAttribute('data-atc-base-text') || '';
      var target = button.querySelector('.button-txt') || button;
      target.textContent = label ? label + ' ' + money(base + addonCents) : money(base + addonCents);
    });
  };

  /*
    The contract the upsell sheet reads on open.

    Without this the sheet opens on its own free-buckle default and silently
    contradicts the customer: someone who picked a gold clasp on the page is
    shown the free buckle again and can end up adding a second closure. The old
    <product-addons> element published the same method, and replacing it is what
    broke the carry-over.

    Only variantId is consumed today. isFree is included because the sheet
    distinguishes a paid choice from the panel's own free default when deciding
    what to treat as a decision.
  */
  Panel.prototype.getSelectedAddonSelections = function () {
    var out = [];
    this.root.querySelectorAll('[data-wb-tile-input]:checked').forEach(function (input) {
      var tile = input.closest('[data-wb-tile]');
      if (!tile) return;
      var variantId = Number(tile.getAttribute('data-wb-variant-id') || input.value);
      if (!Number.isFinite(variantId)) return;
      out.push({
        variantId: variantId,
        isFree: tile.getAttribute('data-wb-free') === 'true',
        price: Number(tile.getAttribute('data-wb-price')) || 0,
      });
    });
    return out;
  };

  /*
    The signal that the theme has finished rendering a new variant.

    renderProductInfo fetches the section, swaps the buy column and this panel's
    markup into place, and ends by calling window.pmUpdateMainAtcPriceLabel().
    It dispatches nothing afterwards, and `variant:changed` fires at the start of
    all that, before any of the new markup exists, so that event cannot be the
    signal. This call can: it runs last and exactly once per render.

    It is also the call that breaks the add-to-cart price. ProductAddonsPanel
    totals `product-addons [data-addon-card]`, which this panel does not emit, so
    it reads zero add-ons and rewrites the button back to the bare variant price
    while a paid clasp is still selected and still in the cart properties.
    Re-syncing here puts the panel's own total back over the top of it.
  */
  var hooked = false;

  function hookVariantRender() {
    if (hooked) return;
    var original = window.pmUpdateMainAtcPriceLabel;
    // Absent for now, or for good if product-addons.min.js failed to load, in
    // which case there is no label being rewritten and nothing to hook.
    if (typeof original !== 'function') return;
    hooked = true;

    window.pmUpdateMainAtcPriceLabel = function () {
      var result = original.apply(this, arguments);
      document.querySelectorAll('[data-pdp-addons]').forEach(function (root) {
        if (root.__pdpAddons) root.__pdpAddons.adopt();
      });
      return result;
    };
  }

  /*
    Both files are deferred and this one is first, so at the moment a deferred
    script runs, product-addons.min.js has not defined the function yet. Trying
    again once the document is ready is what actually lands the hook; the guard
    above makes the repeats free.
  */
  function ensureHook() {
    hookVariantRender();
    if (hooked) return;
    document.addEventListener('DOMContentLoaded', hookVariantRender, { once: true });
    window.addEventListener('load', hookVariantRender, { once: true });
  }

  function start() {
    ensureHook();
    document.querySelectorAll('[data-pdp-addons]').forEach(function (root) {
      if (root.__pdpAddons) return;
      var panel = new Panel(root);
      root.__pdpAddons = panel;
      // The sheet calls this on the element, not on the instance.
      root.getSelectedAddonSelections = function () {
        return panel.getSelectedAddonSelections();
      };
    });
  }

  /*
    Exposed so a surface that injects a panel after load can initialise it. The
    bundle page fetches this panel from a strap's own product page, because the
    panel is driven by that product's metafields and a collection page has no
    product object. start() skips panels it has already claimed, so calling it
    again is safe.
  */
  window.wbPdpAddons = { start: start };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /*
    A colour swatch change re-renders the buy column, which replaces the panel.
    Re-running adopts the new one; the guard above keeps a surviving element
    from being bound twice.
  */
  function onVariantChange() {
    // Fired before the section fetch resolves, which is the only moment the
    // current selection still exists to be remembered.
    ensureHook();
    document.querySelectorAll('[data-pdp-addons]').forEach(function (root) {
      if (root.__pdpAddons) root.__pdpAddons.rememberPaidSelections();
    });
    start();
  }

  document.addEventListener('variant:changed', onVariantChange);
  document.addEventListener('product:variant-change', onVariantChange);
})();
